import React, { useMemo } from 'react';
import styled from 'styled-components';

import type { KaraokeConfig } from '@src/editor/models/karaoke/KaraokeConfig';
import type { KaraokeLine } from '@src/editor/types/karaoke';

export interface ReviewCaptionOverlayProps {
  /** The review editor's caption track, as lines. */
  lines: readonly KaraokeLine[];
  /** The track's style (the editor's default karaoke config). */
  config: KaraokeConfig | null;
  /** The player's clock, in the lines' own seconds. */
  timeSec: number;
}

/**
 * PLAT-11683 — captions over the STORY-entry review player. The story entry
 * plays a plain video (no composition renderer), so the review draws the
 * caption track itself: the line under the playhead, styled from the
 * track's karaoke config the way the composition would style it — font,
 * weight, colours, stroke, shadow, transform, width and position — with the
 * composition's viewport units mapped onto the player frame.
 *
 * A sibling of the player inside `PlayerFrame` (already `position: relative;
 * overflow: hidden`), pointer-events off so the player keeps every click. The
 * overlay is its own size container, so the config's `vh` / `vmin` read
 * against the frame exactly as they read against the composition.
 */
export const ReviewCaptionOverlay: React.FC<ReviewCaptionOverlayProps> = ({
  lines,
  config,
  timeSec,
}) => {
  const line = useMemo(
    () => resolveActiveCaptionLine(lines, timeSec),
    [lines, timeSec],
  );
  const text = line ? captionLineText(line) : '';
  const style = useMemo(
    () => (config ? captionStyleFromConfig(config) : {}),
    [config],
  );
  if (!text) return null;
  return (
    <Overlay data-testid="review-caption-overlay" aria-hidden="true">
      <Line style={style}>{text}</Line>
    </Overlay>
  );
};

/** The line spoken at `timeSec`; `null` in a pause. */
export function resolveActiveCaptionLine(
  lines: readonly KaraokeLine[],
  timeSec: number,
): KaraokeLine | null {
  for (const line of lines) {
    const start = line.transcriptStartTime + (line.startSecondsOffset ?? 0);
    const end = line.transcriptEndTime + (line.endSecondsOffset ?? 0);
    if (timeSec >= start && timeSec < end) return line;
  }
  return null;
}

/**
 * What the line shows: the resolved translation when the track was produced
 * for a language, else its visible source words.
 */
export function captionLineText(line: KaraokeLine): string {
  if (line.translatedText != null) return line.translatedText.trim();
  const hidden = new Set(line.allHiddenTranscriptTokenIds ?? []);
  return line.transcriptTokens
    .filter(token => !hidden.has(token.id) && token.value != null)
    .map(token => token.value)
    .join('')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Every composition this app renders is 1080 tall (`getDimensionSize`), so a
 * pixel length in a karaoke config is a fraction of that height.
 */
const COMPOSITION_HEIGHT_PX = 1080;

/**
 * A composition length in the player frame: the composition's viewport units
 * become the overlay's container units (`3.333vh` → `3.333cqh`); pixels —
 * bare (`'36'`, what a saved template carries) or `36px` — become the same
 * fraction of the frame's height they were of the composition's; percentages
 * pass through.
 */
export function toFrameUnit(value: string | number | null | undefined): string {
  if (value == null || value === '') return '';
  const raw = String(value).replace(/\s+/g, '');
  const px = /^(-?\d*\.?\d+)(px)?$/.exec(raw);
  if (px) {
    const cqh = (parseFloat(px[1]) / COMPOSITION_HEIGHT_PX) * 100;
    return `${Math.round(cqh * 1000) / 1000}cqh`;
  }
  return raw.replace(/v(h|w|min|max)$/, 'cq$1');
}

/**
 * The review frame is a fraction of the composition's size (360px wide in
 * Translation Review), where a size that is right on the composition reads
 * small. Drawn a little larger than proportional, and never below a legible
 * floor (Fernando, Sep 14).
 */
const FONT_SCALE = 1.2;
const FONT_FLOOR = '12px';
export function readableFontSize(frameSize: string): string {
  return `max(calc(${frameSize} * ${FONT_SCALE}), ${FONT_FLOOR})`;
}

/** The CSS a karaoke config amounts to, for one caption line. */
export function captionStyleFromConfig(
  config: KaraokeConfig,
): React.CSSProperties {
  const background =
    config.background_color && config.background_color !== 'transparent'
      ? config.background_color
      : undefined;
  const strokeWidth = toFrameUnit(config.stroke_width);
  const shadow =
    config.shadow_color && config.shadow_color !== 'transparent'
      ? `${config.shadow_x ?? 0}px ${config.shadow_y ?? 0}px ${config.shadow_blur ?? 0}px ${config.shadow_color}`
      : undefined;
  const alignment = parseFloat(config.x_alignment ?? '50%');
  return {
    left: toFrameUnit(config.x ?? '50%') || '50%',
    top: toFrameUnit(config.y) || '86%',
    width: toFrameUnit(config.width) || '76%',
    fontFamily: config.font_family || undefined,
    fontWeight: config.font_weight || undefined,
    fontStyle: config.font_style || undefined,
    fontSize: readableFontSize(toFrameUnit(config.font_size) || '3.333cqh'),
    lineHeight: config.line_height
      ? parseFloat(config.line_height) / 100
      : 1.25,
    color: config.fill_color || undefined,
    background,
    borderRadius: background
      ? toFrameUnit(config.background_border_radius) || undefined
      : undefined,
    textAlign: alignment <= 25 ? 'left' : alignment >= 75 ? 'right' : 'center',
    textTransform: (config.text_transform ||
      undefined) as React.CSSProperties['textTransform'],
    WebkitTextStroke:
      strokeWidth && strokeWidth !== '0' && config.stroke_color
        ? `${strokeWidth} ${config.stroke_color}`
        : undefined,
    textShadow: shadow,
  };
}

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  container-type: size;
`;

// Positioned by the config's anchor point (x / y are the block's centre, as
// in the composition), the words wrapped inside the config's width.
const Line = styled.div`
  position: absolute;
  transform: translate(-50%, -50%);
  padding: 0.1em 0.3em;
  white-space: pre-wrap;
  word-break: break-word;
  /* Stroke behind the fill, as the renderer paints it. */
  paint-order: stroke fill;
`;
